import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import ScrollToTop from "@/components/feature/ScrollToTop";
import WorkCard from "@/components/feature/WorkCard";
import Seo from "@/components/feature/Seo";
import { characters } from "@/mocks/characters";
import { works } from "@/mocks/works";

export default function Characters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("c") || "";

  const selected = characters.find((c) => c.id === selectedId);

  const relatedWorks = useMemo(
    () => (selected ? works.filter((w) => w.characterId === selected.id) : []),
    [selected],
  );

  const pageTitle = selected
    ? `${selected.name}同人专区 - 火影忍者${selected.name}同人作品`
    : "火影忍者同人角色专区 - 雏田、樱、纲手、佐助、鸣人同人分类";

  return (
    <>
      <ScrollToTop />
      <Seo
        title={pageTitle}
        description={
          selected
            ? `浏览${selected.name}同人作品专区，收录${selected.count}部${selected.name}同人，涵盖${selected.title}，为你整理最值得一读的${selected.name}二次创作。`
            : "火影同人志角色专区，按角色分类收录日向雏田同人、春野樱同人、纲手同人、宇智波佐助同人、漩涡鸣人同人等热门火影忍者同人作品。"
        }
        keywords="火影忍者同人,角色专区,日向雏田同人,春野樱同人,纲手同人,宇智波佐助同人,漩涡鸣人同人"
      />

      <Navbar />

      {/* Page header */}
      <section className="bg-secondary-700">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-secondary-200">
            <Link to="/" className="hover:text-primary-300">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span>角色专区</span>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-4xl">
            火影忍者同人 · 角色专区
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-secondary-100 md:text-base">
            以角色为核心的同人分类。选择你钟爱的角色，即可查看专属的同人作品合集，从雏田的温柔告白到佐助的黑暗归途，每一种可能都在这里。
          </p>
        </div>
      </section>

      {/* Character selector grid */}
      <section className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-4">
          {characters.map((c) => {
            const active = c.id === selectedId;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setSearchParams(active ? {} : { c: c.id })}
                className={`group flex items-center gap-3 rounded-lg border p-3 text-left transition-all md:gap-4 md:p-4 ${
                  active
                    ? "border-primary-500 bg-primary-50"
                    : "border-background-200 bg-background-100 hover:border-primary-300"
                }`}
              >
                <img
                  src={c.image}
                  alt={`${c.name}同人角色头像`}
                  title={`${c.name}火影忍者同人`}
                  loading="lazy"
                  className="h-14 w-14 flex-shrink-0 rounded-md object-cover object-top md:h-16 md:w-16"
                />
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold text-foreground-950 md:text-base">{c.name}</p>
                  <p className="mt-0.5 truncate text-xs text-foreground-600">{c.count} 部同人</p>
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {selected ? (
        <section className="mx-auto w-full max-w-7xl px-4 pb-20 md:px-6">
          {/* Selected character hero */}
          <div className="grid grid-cols-1 gap-6 rounded-lg bg-background-100 p-6 md:grid-cols-3 md:p-8 lg:grid-cols-4">
            <div className="relative aspect-[4/5] overflow-hidden rounded-lg md:col-span-1">
              <img
                src={selected.image}
                alt={`${selected.name} - ${selected.nameJp}同人角色`}
                title={`${selected.name}同人作品`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
                {selected.count} 部作品
              </span>
            </div>
            <div className="flex flex-col justify-center md:col-span-2 lg:col-span-3">
              <p className="text-xs font-bold uppercase tracking-widest text-accent-500">
                {selected.nameJp}
              </p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {selected.name}
                <span className="ml-3 text-sm font-medium text-foreground-500">· {selected.title}</span>
              </h2>
              <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {selected.description}
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {relatedWorks.slice(0, 5).map((w) => (
                  <span key={w.id} className="rounded bg-background-200 px-3 py-1 text-xs text-foreground-700">
                    {w.title}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Related works */}
          <div className="mt-8 flex items-center justify-between">
            <h3 className="font-heading text-xl font-black text-foreground-950 md:text-2xl">
              {selected.name}同人作品
            </h3>
            <span className="text-sm text-foreground-600">共 {relatedWorks.length} 部</span>
          </div>

          {relatedWorks.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
              {relatedWorks.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          ) : (
            <p className="mt-6 rounded-lg bg-background-100 p-8 text-center text-sm text-foreground-600">
              该角色的同人作品正在整理中，敬请期待。
            </p>
          )}
        </section>
      ) : (
        <section className="mx-auto w-full max-w-7xl px-4 pb-20 text-center md:px-6">
          <p className="rounded-lg bg-background-100 p-10 text-sm text-foreground-600 md:text-base">
            点击上方任意角色卡片，即可查看该角色的火影忍者同人作品合集。
          </p>
        </section>
      )}

      <Footer />
      <BackToTop />
    </>
  );
}